import React from 'react';
import { AlertCircle } from 'lucide-react';

interface VariantSelectorProps {
  colors?: string[];
  sizes?: string[];
  selectedColor: string | null;
  selectedSize: string | null;
  onSelectColor: (color: string | null) => void;
  onSelectSize: (size: string | null) => void;
  variantError: string | null;
}

const colorMap: Record<string, string> = {
  red: '#ef4444', green: '#22c55e', blue: '#3b82f6',
  black: '#1f2937', white: '#f9fafb', yellow: '#eab308',
  purple: '#a855f7', pink: '#ec4899', orange: '#f97316',
  brown: '#92400e', gray: '#6b7280', grey: '#6b7280',
  silver: '#e5e7eb', gold: '#fbbf24', navy: '#1e3a5f',
  beige: '#f5f5dc', maroon: '#800000', teal: '#14b8a6',
  coral: '#f97316', lavender: '#a78bfa', mint: '#6ee7b7',
};

const VariantSelector: React.FC<VariantSelectorProps> = ({
  colors = [],
  sizes = [],
  selectedColor,
  selectedSize,
  onSelectColor,
  onSelectSize,
  variantError,
}) => {
  const validColors = colors.filter(c => c && c.trim());
  const validSizes = sizes.filter(s => s && s.trim());

  const isLightColor = (color: string) => {
    const lightColors = ['white', 'silver', 'beige', 'yellow', 'mint', 'gold', 'lavender'];
    return lightColors.includes(color.toLowerCase());
  };

  return (
    <div className="space-y-3 sm:space-y-3 overflow-hidden w-full min-w-0">
      {/* Colors */}
      {validColors.length > 0 && (
        <div className="overflow-hidden">
          <label className="block text-xs sm:text-xs font-bold text-gray-800 mb-2.5">
            Color: <span className="text-gray-900 font-bold break-words">{selectedColor || 'Select a color'}</span>
          </label>
          <div className="flex flex-wrap gap-2.5 sm:gap-2.5 overflow-hidden">
            {validColors.map((color) => {
              const bgColor = colorMap[color.toLowerCase()] || color.toLowerCase();
              const isSelected = selectedColor === color;
              return (
                <button
                  key={color}
                  onClick={() => onSelectColor(isSelected ? null : color)}
                  className={`group relative w-10 h-10 sm:w-9 sm:h-9 rounded-xl border-2 transition-all duration-300 flex items-center justify-center flex-shrink-0 ${
                    isSelected
                      ? 'border-blue-600 ring-2 ring-blue-200 scale-105 shadow-md shadow-blue-500/15'
                      : 'border-gray-200 hover:border-gray-300 hover:scale-105 hover:shadow-md'
                  }`}
                  style={{ backgroundColor: bgColor }}
                  title={color}
                >
                  {isSelected && (
                    <svg
                      className={`w-5 h-5 drop-shadow-md ${isLightColor(color) ? 'text-gray-800' : 'text-white'}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sizes */}
      {validSizes.length > 0 && (
        <div className="overflow-hidden">
          <label className="block text-xs sm:text-xs font-bold text-gray-800 mb-2.5">
            Size: <span className="text-gray-900 font-bold break-words">{selectedSize || 'Select a size'}</span>
          </label>
          <div className="flex flex-wrap gap-2 overflow-hidden">
            {validSizes.map((size) => (
              <button
                key={size}
                onClick={() => onSelectSize(selectedSize === size ? null : size)}
                className={`min-w-[3rem] px-3 py-1.5 sm:px-3 sm:py-1.5 rounded-xl border-2 text-xs sm:text-xs font-bold transition-all active:scale-95 flex-shrink-0 ${
                  selectedSize === size
                    ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-md shadow-blue-500/20'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Variant Error */}
      {variantError && (
        <div className="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl p-2.5 sm:p-2 flex items-center gap-2 animate-in fade-in slide-in-from-top-1 overflow-hidden shadow-md">
          <AlertCircle className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-red-500 flex-shrink-0" />
          <span className="text-xs sm:text-xs text-red-700 font-bold break-words">{variantError}</span>
        </div>
      )}
    </div>
  );
};

export default VariantSelector;
